import {
  TaxRegistrationAddressType,
  TaxRegistrationShareholderType,
  type TaxRegistrationRepresentative,
  type TaxRegistrationShareholder,
} from "./types";

/**
 * Zeilen 68 to 78 have no Anrede field: the Anteilseigner schema carries
 * firstName, lastName, dateOfBirth and an address, but nothing for salutation
 * (that only exists on the Gesetzliche Vertreter, Zeilen 25 to 30). So the copy
 * below leaves `salutation` out on purpose, not as an oversight: bringing it
 * across would put a field on the shareholder that ELSTER has no line for
 * (SMT-1359).
 */
export const copyRepresentativeIntoShareholder = (
  shareholder: TaxRegistrationShareholder | undefined,
  representative: TaxRegistrationRepresentative
): TaxRegistrationShareholder => ({
  subscriberNumber: shareholder?.subscriberNumber,
  nominal: shareholder?.nominal,
  percentage: shareholder?.percentage,
  type: TaxRegistrationShareholderType.NATURAL,
  firstName: representative.firstName,
  lastName: representative.lastName,
  dateOfBirth: representative.dateOfBirth,
  companyName: undefined,
  businessActivity: undefined,
  address: representative.address
    ? { ...representative.address }
    : { type: TaxRegistrationAddressType.DOMESTIC },
});

/** Whether a representative has enough of a name to be worth offering as a copy source. */
export const hasRepresentativeName = (
  representative: TaxRegistrationRepresentative
): boolean =>
  Boolean(representative.firstName?.trim()) ||
  Boolean(representative.lastName?.trim());

const normalizeName = (value?: string): string =>
  (value ?? "").trim().toLowerCase();

/**
 * Whether a representative already appears as a natural-person shareholder,
 * so the picker can leave them out. This is worked out from the answers on
 * screen rather than remembered from a previous copy: that way it also covers
 * a shareholder the operator typed by hand, not only one this picker created,
 * and it still holds after the record is saved and reloaded, when no memory
 * of the click would survive.
 */
export const isRepresentativeAlreadyShareholder = (
  representative: TaxRegistrationRepresentative,
  shareholders: TaxRegistrationShareholder[] | undefined
): boolean =>
  (shareholders ?? []).some(
    (shareholder) =>
      shareholder.type !== TaxRegistrationShareholderType.LEGAL &&
      normalizeName(shareholder.firstName) ===
        normalizeName(representative.firstName) &&
      normalizeName(shareholder.lastName) ===
        normalizeName(representative.lastName) &&
      shareholder.dateOfBirth === representative.dateOfBirth
  );
